import type { BackgroundNoImageProtocol, ImageInfo, ImageSwiperItem, SizeConfigure } from '@lighthouse/core'
import { DIRECTION, ImageVariableValue } from '@lighthouse/core'
import { getExplicitWidth, getImageFullUrlInApplication, getWidthSize, useFlowLayoutContext } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import FileItem from './FileItem'

interface GridProps {
    noLazy?: boolean
    value: ImageSwiperItem[]
    gridItemWidth?: SizeConfigure
    gridShowCount?: string
    gridGap?: SizeConfigure
    radius?: string
    width: number
    appId: string | undefined
    background?: BackgroundNoImageProtocol
    compress?: boolean
    isClickEvent?: boolean
    handImageClick: (ev: React.MouseEvent, info: ImageInfo) => void
    hasCustomCursor: boolean
}

const GridContainer = styled.div<{ $gap: string }>`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: ${props => props.$gap};
`

const GridItem = styled.div`
    position: relative;
`

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
`

const OverlayText = styled.span`
    color: white;
    font-size: 24px;
    font-weight: 500;
`

export const Grid = (props: GridProps) => {
    const {
        noLazy = false,
        value,
        gridItemWidth,
        gridShowCount,
        gridGap,
        radius,
        width,
        appId,
        background,
        compress,
        isClickEvent,
        handImageClick,
        hasCustomCursor
    } = props
    const { sortable, useParseVariableWithSubscription } = useFlowLayoutContext()

    // 解析 gridItemWidth 中的变量
    const parseGridItemWidth = useParseVariableWithSubscription(gridItemWidth?.variable)
    const resolvedGridItemWidth = useMemo(() => {
        if (!gridItemWidth?.variable) return gridItemWidth
        const resolved = parseGridItemWidth(gridItemWidth.variable)
        if (!resolved) return gridItemWidth
        const numValue = Number((resolved as { value?: unknown }).value)
        if (Number.isFinite(numValue)) {
            return { ...gridItemWidth, size: numValue }
        }
        return gridItemWidth
    }, [gridItemWidth, parseGridItemWidth])

    // const gridItemWidthCss = gridItemWidth ? ((getWidthSize(gridItemWidth, DIRECTION.horizontal, sortable) as { width?: string }).width ?? '72px') : '72px'
    const gridGapCss = gridGap ? (getWidthSize(gridGap, DIRECTION.horizontal, sortable) as { width?: string }).width ?? '12px' : '12px'
    const gridItemWidthPx = resolvedGridItemWidth ? getExplicitWidth(resolvedGridItemWidth, width) : 72

    const showCount = gridShowCount ? Number(gridShowCount) : undefined
    const hasMore = showCount !== undefined && value.length > showCount
    const displayItems = showCount !== undefined ? value.slice(0, showCount) : value
    const remainingCount = showCount !== undefined ? value.length - showCount : 0
    const maxDisplay = showCount ?? value.length

    const borderRadius = useMemo(() => {
        return radius ? `${radius}px` : undefined
    }, [radius])

    return (
        <GridContainer $gap={gridGapCss}>
            {displayItems.map((item, index) => {
                const isLastItem = hasMore && index === maxDisplay - 1
                const imageInfo = {
                    [ImageVariableValue.IMAGE_INDEX]: index,
                    [ImageVariableValue.IMAGE_URL]: item.url,
                    [ImageVariableValue.IMAGE_NAME]: item.title
                }
                return (
                    <GridItem
                        key={item.url || `grid-${index}`}
                        style={{
                            width: gridItemWidthPx,
                            height: gridItemWidthPx,
                            borderRadius,
                            overflow: 'hidden',
                            cursor: isClickEvent || hasCustomCursor ? undefined : 'pointer'
                        }}
                    >
                        <FileItem
                            noLazy={noLazy}
                            src={getImageFullUrlInApplication(appId, item.url, {
                                width: gridItemWidthPx,
                                compress
                            })}
                            onClick={ev => handImageClick(ev, imageInfo)}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                        {isLastItem && (
                            <Overlay onClick={ev => handImageClick(ev, imageInfo)}>
                                <OverlayText>+{remainingCount}</OverlayText>
                            </Overlay>
                        )}
                    </GridItem>
                )
            })}
        </GridContainer>
    )
}
